import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Form, Input, Card, CardBody, CardHeader, Button, Col, Row } from 'reactstrap';
import MaterialCheckbox from '../../../../shared/components/material-checkbox/MaterialCheckbox';
import BrandCombobox from '../../../../shared/components/searchable-field/BrandCombobox';
import DataTableProgressComponent from '../../../../shared/components/widget/DataTableProgressComponent';
import DataTableNoDataComponent from '../../../../shared/components/widget/DataTableNoDataComponent';
import DropdownList from 'react-widgets/lib/DropdownList';
import { Link } from 'react-router-dom';
import 'react-widgets/dist/css/react-widgets.css';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Fade from '@material-ui/core/Fade';
import DataTable from 'react-data-table-component';
import { deleteTransaction, getTransactionCustomer, getTransactionsCustomer } from '../../../../actions/transactionActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';
import BrandModal from './BrandModal';
const columns = (customerId, handleEditClick, handleShowClick, handleDeleteClick, handleBrandClick) => [
  {
    name: 'ID',
    selector: 'id',
    sortable: false,
    center: true,
    grow: 0
  },
  {
    name: 'Reference',
    selector: 'reference',
    sortable: false,
    grow: 1
  },
  {
    name: 'Brand',
    selector: "brand",
    sortable: true,
    cell: row => (
      <button 
        className="btn btn-link p-0 text-left"
        style={{ border: 'none', background: 'none', color: '#007bff', textDecoration: 'underline' }}
        onClick={() => handleBrandClick(row.brand?.id)}
      >
        {row.brand != null ? row.brand.name : '---'}
      </button>
    ),
    grow: 1
  },
  !customerId && {
    name: 'Customer',
    selector: 'customer_id',
    sortable: false,
    grow: 1
  },
  {
    name: 'Type',
    selector: 'type',
    sortable: true,
    right: false,
    grow: 0
  },
  {
    name: 'Booked at',
    selector: "booked_at",
    sortable: false,
    cell: row => <div>{row.booked_at != null ? row.booked_at.substring(0, 10) : ''}</div>,
    grow: 1
  },
  {
    name: 'Amount',
    selector: "amount",
    sortable: false,
    cell: row => <div className="text-right">{row.amount}</div>,
    grow: 1
  },
  {
    name: 'Status',
    selector: 'status',
    sortable: true,
    grow: 1
  },
  {
    name: 'Notes',
    selector: 'notes',
    sortable: false,
    grow: 1
  },
  {
    name: 'Actions',
    sortable: false,
    left: true,
    cell: (row) => <div>
      <button className="btn btn-sm btn-light mr-2" onClick={(e) => handleEditClick(row.id)}>
        <FontAwesomeIcon icon="pencil" />
      </button>
      <button className="btn btn-sm btn-light mr-2" onClick={(e) => handleDeleteClick(row.id)}>
        <FontAwesomeIcon icon="trash" />
      </button>
      <button className="btn btn-sm btn-light mr-2" onClick={(e) => handleShowClick(row.id)}>
        <FontAwesomeIcon icon="book-open" />
      </button>
    </div>,
    ignoreRowClick: true
  }
].filter(Boolean);

const selectProps = { indeterminate: isIndeterminate => isIndeterminate };

const Transactions = ({ customerId }) => {
  const history = useHistory();
  const location = useLocation();
  // State hooks
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sort, setSort] = useState(false);
  const [search, setSearch] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [modalTitle, setModalTitle] = useState(null);
  const [error, setError] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const dispatch = useDispatch();
  const [showSearch, setShowSearch] = useState(false);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(null);

  // Get bank ID from URL
  useEffect(() => {
    const bankId = new URLSearchParams(location.search).get("bank_id");
    if (bankId) {
      setSelectedBank(parseInt(bankId));
    }
  }, [location.search]);

  const fetchTransactions = useCallback(async () => {
    if (!selectedBank) return;

    setLoading(true);
    try {
      const params = {
        per_page: perPage,
        'search[customer]': customerId,
        bank_id: selectedBank,
        ...(sort && sort),
        ...(search && search)
      };

      const result = await getTransactionsCustomer(params);
      setData(result.items || []);
      setTotalRows(result.meta?.total_items || 0);
    } catch (error) {
      setError(error);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  }, [selectedBank, perPage, sort, search, customerId]);

  // Load transactions when bank is selected
  useEffect(() => {
    if (selectedBank) {
      setShowSearch(true);
      fetchTransactions();
    }
  }, [selectedBank, fetchTransactions, customerId]);

  const handleEditClick = useCallback((id) => {
    history.push({
      pathname: `/transactions/edit/${id}/${selectedBank}`,
      search: `?${new URLSearchParams({ bank_id: selectedBank }).toString()}`
    });
  }, [history, selectedBank]);

  const handleDeleteClick = useCallback(async (id) => {
    if (window.confirm('Are you sure you want to delete this transaction?')) {
      try {
        dispatch(deleteTransaction(selectedBank, id)).then(() =>  window.location.reload())
       
      } catch (error) {
        setError(error);
        setModalOpen(true);
      }
    }
  }, [selectedBank, dispatch]);

  const handleBrandClick = useCallback((brandId) => {
    if (brandId) {
      setSelectedBrandId(brandId);
      setBrandModalOpen(true);
    }
  }, []);

  const handleShowClick = useCallback(async (id) => {
    try {
      const result = await getTransactionCustomer({ id, bank_id: selectedBank });
      setTransaction(result);
      setModalTitle('Transaction Details');
      setModalOpen(true);
    } catch (error) {
      setError(error);
      setModalOpen(true);
    }
  }, [selectedBank]);

  const handlePageChange = useCallback(async (page) => {
    setLoading(true);
    try {
      const params = {
        per_page: perPage,
        page,
         'search[customer]': customerId,
        bank_id: selectedBank,
        ...(sort && sort),
        ...(search && search)
      };

      const result = await getTransactionsCustomer(params);
      setData(result.items || []);
      setTotalRows(result.meta?.total_items || 0);
    } catch (error) {
      setError(error);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  }, [perPage, selectedBank, sort, search]);

  const handlePerRowsChange = useCallback(async (newPerPage, page) => {
    setLoading(true);
    try {
      const params = {
        per_page: newPerPage,
        page,
         'search[customer]': customerId,
        bank_id: selectedBank,
        ...(sort && sort),
        ...(search && search)
      };

      const result = await getTransactionsCustomer(params);
      setData(result.items || []);
      setTotalRows(result.meta?.total_items || 0);
      setPerPage(newPerPage);
    } catch (error) {
      setError(error);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  }, [selectedBank, sort, search]);

  const handleSort = useCallback(async (column, sortDirection) => {
    setLoading(true);
    try {
      const newSort = { [`order[${column.selector}]`]: sortDirection };
      const params = {
        per_page: perPage,
        bank_id: selectedBank,
         'search[customer]': customerId,
        ...newSort,
        ...(search && search)
      };

      const result = await getTransactionsCustomer(params);
      setData(result.items || []);
      setTotalRows(result.meta?.total_items || 0);
      setSort(newSort);
    } catch (error) {
      setError(error);
      setModalOpen(true);
    } finally {
      setLoading(false);
    }
  }, [perPage, selectedBank, search]);

  const onSearchInputChange = useCallback((evt, field) => {
    const newSearch = { ...search };

    if (field) {
      // Handle special fields
      let newEvt = evt;

      if (field === 'status') {
        const statusMap = {
          'Pending': 'pending',
          'Confirmed': 'confirmed',
          'Declined': 'declined',
          'Paid': 'paid'
        };
        newEvt = { target: { name: 'status', value: statusMap[evt] || '' } };
      }

      if (field === 'type') {
        const typeMap = {
          'Deposit': 'deposit',
          'Withdraw': 'withdraw'
        };
        newEvt = { target: { name: 'type', value: typeMap[evt] || '' } };
      }

      if (field === 'online') {
        newEvt = { target: { name: 'online', value: evt === 'Online' } };
      }

      evt = newEvt;
    }

    if (evt.target.value !== '' || evt.target.name === 'online') {
      newSearch[`search[${evt.target.name}]`] = encodeURIComponent(evt.target.value);
    } else {
      delete newSearch[`search[${evt.target.name}]`];
    }

    setSearch(newSearch);
  }, [search]);

  const onSearchableInputChange = useCallback((name, value) => {
    const newSearch = { ...search };

    if (value !== '' && value !== 0) {
      newSearch[`search[${name}]`] = encodeURIComponent(value);
    } else {
      delete newSearch[`search[${name}]`];
    }

    console.log(newSearch);

    setSearch(newSearch);
  }, [search]);

  const handleFindTransactions = useCallback((e) => {
    e.preventDefault();
    if (search && Object.keys(search).length > 0) {
      fetchTransactions();
    }
  }, [search, fetchTransactions]);

  const handleChange = (state) => {
    console.log('Selected Rows: ', state.selectedRows);
  };

  const tableColumns = useMemo(() =>
    columns(customerId, handleEditClick, handleShowClick, handleDeleteClick, handleBrandClick),
    [customerId, handleEditClick, handleShowClick, handleDeleteClick, handleBrandClick]
  );

  return (
    <Col className="animated fadeIn mt-5">
      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        id="modal"
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          <div className="material-modal-content">
            <h1 id="transition-modal-title">
              {modalTitle}
              <FontAwesomeIcon
                icon="times"
                className="cursor-pointer float-right"
                onClick={() => setModalOpen(false)}
              />
            </h1>
            <div id="transition-modal-description">
              {transaction && (
                <div className="container transactions-modal">
                  <div className="row card">
                    <div className="col-12 col-sm-12">
                      <table className="table mt-4">
                        <tbody>
                          <tr>
                            <th>ID:</th>
                            <td>{transaction.id}</td>
                          </tr>
                          <tr>
                            <th>Reference:</th>
                            <td>{transaction.reference}</td>
                          </tr>
                          <tr>
                            <th className="w-50">Brand:</th>
                            <td>{transaction.brand?.name || '---'}</td>
                          </tr>
                          <tr>
                            <th className="w-50">Customer:</th>
                            <td>{transaction.customer_id}</td>
                          </tr>
                          <tr>
                            <th className="w-50">Type:</th>
                            <td>{transaction.type}</td>
                          </tr>

                           <tr>
                            <th className="w-50">Offer Id:</th>
                            <td>{transaction?.offer?.id}</td>
                          </tr>
                          <tr>
                            <th className="w-50">Status:</th>
                            <td>{transaction.status}</td>
                          </tr>
                          <tr>
                            <th>Amount:</th>
                            <td>{transaction.amount}</td>
                          </tr>
                          <tr>
                            <th>Commission:</th>
                            <td>{transaction.commission || '0.0'}</td>
                          </tr>
                          <tr>
                            <th>Net Amount:</th>
                            <td>{transaction.net_amount || '---'}</td>
                          </tr>
                          <tr>
                            <th>Paid Amount:</th>
                            <td>{transaction.paid_amount || '---'}</td>
                          </tr>
                          <tr>
                            <th>Gross Amount:</th>
                            <td>{transaction.gross_amount || '---'}</td>
                          </tr>
                          <tr>
                            <th>Payment ID:</th>
                            <td>{transaction.payment_id || ''}</td>
                          </tr>
                          <tr>
                            <th>Booked at:</th>
                            <td>{transaction.booked_at ? transaction.booked_at.substring(0, 10) : ''}</td>
                          </tr>
                          <tr>
                            <th className="w-50">Notes:</th>
                            <td>{transaction.notes}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Fade>
      </Modal>

      <BrandModal 
        open={brandModalOpen}
        onClose={() => setBrandModalOpen(false)}
        brandId={selectedBrandId}
      />

      {/* <Row>
        <Col xs="12" lg="2">
          <Combobox
            placeholder="Choose Bank"
            data={banksArr}
            valueField="id"
            textField="name"
            defaultValue={{
              id: selectedBank || 0, 
              name: (selectedBank && banksArr.length > 0) ? 
                banksArr.find(b => b.id === selectedBank)?.name || '' : ''
            }}
            type="select"
            filter={false}
            asterisk="*"
            className="mb-4"
            onChange={handleBankFilterChange}
          />
        </Col>

        {selectedBank && (
          <Col style={{ padding: 0 }} xs="12" lg="2">
            <button 
              className="rw-input" 
              onClick={csvFile}
              style={{ borderRadius: '4px', height: '34px' }}
            >
              Export
            </button>
          </Col>
        )}
      </Row> */}

      <Row>
        <Col xs="12" lg="12">
          <Card>
            <CardHeader>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  Transactions list
                </div>
                {showSearch && (
                  <div>
                    <Form className="form-inline float-right text-right items-end" style={{ gap: 8}} onSubmit={handleFindTransactions}>
                      <div >
                        <label htmlFor="start_date">Time Period From</label>
                        <Input
                          type="date"
                          name="start_date"
                          id="start_date"
                          className="text-left mr-0"
                          placeholder="Time Period From"
                          onChange={(e) => onSearchInputChange(e, 'start_date')}
                        />
                      </div>
                      <div >
                        <label htmlFor="end_date">Time Period To</label>
                        <Input
                          type="date"
                          name="end_date"
                          className="text-left mr-0"
                          onChange={(e) => onSearchInputChange(e, 'end_date')}
                        />
                      </div>

                      <BrandCombobox
                        onChange={onSearchableInputChange}
                        className="w-200 mr-0 brand-box"
                      
                      />
                      <Input
                        type="search"
                        name="reference"
                        className="mr-0 text-left"
                        placeholder="Reference"
                        onChange={onSearchInputChange}
                      />
                      <DropdownList
                        name="type"
                        onChange={(e) => onSearchInputChange(e, 'type')}
                        data={['All types', 'Deposit', 'Withdraw']}
                        placeholder="Type"
                        className="status-dropdown text-left mr-0"
                      />
                      <DropdownList
                        name="online"
                        onChange={(e) => onSearchInputChange(e, 'online')}
                        data={['Online', 'Offline']}
                        placeholder="Online/Offline"
                        className="status-dropdown text-left mr-0"
                      />
                      <DropdownList
                        name="status"
                        onChange={(e) => onSearchInputChange(e, 'status')}
                        data={['All statuses', 'Pending', 'Confirmed', 'Declined', 'Paid']}
                        placeholder="Status"
                        className="status-dropdown text-left mr-0"
                      />
                      <button
                        onClick={handleFindTransactions}
                        className="search-btn cursor-pointer btn btn-outline-secondary"
                        type="button"
                      >
                        <FontAwesomeIcon icon="magnifying-glass" />
                      </button>
                    </Form>
                  </div>
                )}
              </div>
            </CardHeader>
            <div>

              {/* <Link class="btn btn-primary ml-3">Add Transaction</Link> */}
            </div>
            <CardBody>
              <div style={{
                overflowX: 'auto',
                width: '100%',
                maxWidth: '100vw',
                display: 'flex',
                flexDirection: 'column',
              }}>
                <Link to={`/transactions/new?id=${selectedBank}&customer=${customerId}`} className="ml-auto">
                  <Button color="primary">
                    Add Transaction
                  </Button>
                </Link>
                <DataTable
                  noHeader={true}
                  columns={tableColumns}
                  data={data}
                  theme="solarized"
                  progressPending={loading}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationRowsPerPageOptions={[10, 15, 30, 50]}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  sortServer
                  selectableRows
                  selectableRowsComponent={MaterialCheckbox}
                  selectableRowsComponentProps={selectProps}
                  onSelectedRowsChange={handleChange}
                  progressComponent={<DataTableProgressComponent />}
                  noDataComponent={<DataTableNoDataComponent />}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

export default Transactions;